import { FC, ReactElement, useMemo } from 'react';
import styled from 'styled-components';
import ChevronLeftIcon from "../assets/chevron_left.svg?react";
import ChevronRightIcon from "../assets/chevron_right.svg?react";

interface PaginationProps {
  currentPage: number,
  itemCount: number,
  pageSize?: number,
  setCurrentPage: any
}

const PaginationWrapper = styled.ul`
  margin: 30px 0;
  padding: 0 25px;
  list-style: none;
  display: flex;
  justify-content: center;
  flex-wrap: nowrap;
  overflow: auto;
  align-items: flex-start;
  
  &::-webkit-scrollbar {
    display: none;
  }

  &.manyItems li:not(.priority):not(.active) {
    display none;
  }

  @media (max-width: 500px) {
    &.manyItems {
      padding-left: 50px;
    }
  }
`

const PaginationItem = styled.li`
  width: 3.5vw;
  height: 3.5vw;
  padding: 0 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  text-align: center;
  flex: 0 0 auto;
  cursor: pointer;
  overflow: hidden;

  & svg {
    fill: ${p => p.theme.textColor};
  }

  &.active {
    background-color: ${p => p.theme.accentColor};
  }

  &:active {
    background-color: ${p => p.theme.accentColor}50;
  }
`;

const DummyItem = styled.li`
  width: 60px;
  height: 60px
`;

/**
 * Render a pagination component
 * @returns {JSX.Element} Component template
 */
const Pagination: FC<PaginationProps> = ({currentPage, itemCount, pageSize, setCurrentPage}) => {

  /**
   * Render pagination items
   */
  const paginationItems = useMemo(() => {
    let items:ReactElement[] = [];
    let pageCount: number = Math.ceil(itemCount/(pageSize || 10) + 1);

    for (let i = 1; i < pageCount; i++) {
      let classList:string[] = [];
      if (i === currentPage) { classList.push("active"); }
      if (i === currentPage - 1) { classList.push("priority"); }
      if (i === currentPage + 1) { classList.push("priority"); }
      if (i === 1) { classList.push("priority"); }
      if (i === pageCount - 1) { classList.push("priority"); }
      items.push(<PaginationItem key={`pagination${i}`} className={classList?.join(" ") || undefined} onClick={() => setCurrentPage(i)}>{i}</PaginationItem>);
    }

    return items;
  }, [itemCount, pageSize, currentPage, setCurrentPage]);

  // Do not render if only one page is neccesary
  if (itemCount <= (pageSize || 10)) { return null; }

  return (
    <PaginationWrapper className={`showInPreview ${itemCount/(pageSize || 10) > 7 ? "manyItems": "fewItems"}`}>
      {currentPage > 1 ? (<PaginationItem className="priority" onClick={() => setCurrentPage(currentPage - 1)}><ChevronLeftIcon/></PaginationItem>) : <DummyItem/>}
      {paginationItems}
      {currentPage < (itemCount / (pageSize || 10)) ? (<PaginationItem className="priority" onClick={() => setCurrentPage(currentPage + 1)}><ChevronRightIcon/></PaginationItem>) : <DummyItem/>}
    </PaginationWrapper>
  );
}

export default Pagination;